import { useEffect, useState } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router';
import { useAuth } from 'contexts/AuthContext';
import Team from 'pages/Team';
import { ChangePassword } from 'pages/ChangePassword';
import ManageUnits from 'pages/ManageUnits';
import TransferUnits from 'pages/TransferUnits';
import CompletedEvaluators from 'pages/CompletedEvaluators/CompletedEvaluators';
import CreateGroup from 'pages/CreateGroup/CreateGroup';
import Group from 'pages/Group/Group';
import EditGroup from 'pages/EditGroup/EditGroup';
import { DEFAULT_ROUTE, DIALECT_CODES } from 'variables';
import FloatingButton from 'components/FloatingButton';
import QuestionMark from '@mui/icons-material/QuestionMark';
import { styled } from '@mui/material';
import AddUser from 'pages/AddUser/AddUser';
import { useTranslation } from 'react-i18next';
import AddPractitionerToOrgs from 'pages/AddPractitionerToOrgs/AddPractitionerToOrgs';
import EditEvaluatorLinkLearner from 'pages/EditLearner/EditEvaluatorLinkLearner';
import { useBetaEnabled } from 'features';
import { Dialects } from 'types/dialects';
import { LanguageSelectWrapperProvider } from 'contexts/LanguageSelectWrapperContext';
import { AddToTeamFromEvents } from 'pages/AddToTeam/AddToTeamFromEvents';
import Dashboard from './pages/Dashboard';
import Events from './pages/Events';
import CreateEvent from './pages/CreateEvent';
import CreateTeam from './pages/CreateTeam';
import Event from './pages/Event';
import EventLearners from './pages/EventLearners';
import LearnersAndContributors from './pages/LearnersAndContributors';
import AddOrganisation from './pages/AddOrganisation';
import Organisations from './pages/Organisations';
import Learners from './pages/Learners';
import Learner from './pages/Learner';
import EditLearner from './pages/EditLearner';
import EditOrganisation from './pages/EditOrganisation';
import SignInPassword from './pages/SignInPassword';
import SignInStart from './pages/SignInStart';
import ForgottenPassword from './pages/ForgottenPassword';
import SignUp from './pages/SignUp';
import PasswordReset from './pages/PasswordReset';
import Onboard from './pages/Onboard';
import EditEvent from './pages/EditEvent';
import AssignChapters from './pages/AssignChapters';
import DownloadProfiles from './pages/DownloadProfiles';
import DownloadTeamWheel from './pages/DownloadTeamWheel';
import Organisation from './pages/Organisation';
import EditAccount from './pages/EditAccount';
import EditPractitioner from './pages/EditPractitioner';
import Account from './pages/Account';
import Contact from './pages/Contact';
import Thanks from './pages/Contact/Thanks';
import PractitionerAccount from './pages/PractitionerAccount';
import EditTeam from './pages/EditTeam';
import Header from './components/Header';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import EvaluatorLinks from './pages/EvaluatorLinks';
import EvaluatorLinkLearner from './pages/Learner/EvaluatorLinkLearner';
import CreateGroupOrganisation from './pages/CreateGroupOrganisation/CreateGroupOrganisation';
import SsoRedirect from './pages/SsoRedirect';
import { useGATrackPage } from './useGATrackPage';
import { isPreferredDialect } from './domain/dialect';
import { BulkSetPermissions } from './pages/BulkSetPermissions';
import RestrictedRouteError from './components/Errors/RestrictedRouteError';

function isDefaultPath(path: string) {
  return ['', '/'].includes(path);
}

const RouteAwareNavigate = () => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') !== 'false';
  if (redirect && !isDefaultPath(pathname)) {
    sessionStorage.setItem('redirectTo', pathname);
  }
  return <Navigate to="/signin/start" replace />;
};

const HandleUnknownRoute = () => {
  const [redirectTo] = useState(() => sessionStorage.getItem('redirectTo'));
  useEffect(() => {
    if (redirectTo) {
      sessionStorage.removeItem('redirectTo');
    }
  }, [redirectTo]);
  return <Navigate to={redirectTo ?? DEFAULT_ROUTE} />;
};

const UnauthenticatedRoutes = () => (
  <LanguageSelectWrapperProvider>
    <Routes>
      <Route path="/signin" element={<Navigate to="/signin/start" replace />} />
      <Route path="/signin/password" element={<SignInPassword />} />
      <Route path="/signin/start" element={<SignInStart />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/reset-password" element={<PasswordReset />} />
      <Route path="/forgotten-password" element={<ForgottenPassword />} />
      <Route path="/onboard" element={<Onboard />} />
      <Route path="/sso/redirect" element={<SsoRedirect />} />
      <Route path="*" element={<RouteAwareNavigate />} />
    </Routes>
  </LanguageSelectWrapperProvider>
);

const FloatingButtonPosition = styled('div')({
  position: 'fixed',
  right: '1.5rem',
  bottom: '6rem',
});

const StyledDiv = styled('div')({
  flex: '1 0 auto',
});

const AuthenticatedRoutes = ({
  preferredDialect,
}: {
  preferredDialect: Dialects;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  return (
    <>
      <Header />
      <StyledDiv>
        <Routes>
          <Route
            path="/organisations/set-bulk-permissions"
            element={
              <ProtectedRoute
                requiredPermissions={[
                  {
                    action: 'Organisation_Bulk_set_user_permissions',
                    scope: 'Global',
                  },
                ]}
              >
                <BulkSetPermissions />
              </ProtectedRoute>
            }
          />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/experiences" element={<Events />} />
          <Route path="/experiences/create" element={<CreateEvent />} />
          <Route path="/experiences/:eventId" element={<Event />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/contact/thanks" element={<Thanks />} />
          <Route
            path="/experiences/:eventId/learners"
            element={<EventLearners />}
          />
          <Route
            path="/experiences/:eventId/learners-and-contributors"
            element={<LearnersAndContributors />}
          />
          <Route
            path="/experiences/:eventId/assign-chapters"
            element={<AssignChapters />}
          />
          <Route
            path="/experiences/:eventId/download-profiles"
            element={<DownloadProfiles />}
          />
          <Route
            path="/experiences/:eventId/download-team-wheel"
            element={<DownloadTeamWheel type="Experience" />}
          />
          <Route path="/experiences/:eventId/edit/*" element={<EditEvent />} />
          <Route path="/teams/create" element={<CreateTeam />} />
          <Route path="/teams/add-to-team" element={<AddToTeamFromEvents />} />
          <Route path="/teams/:teamId/edit" element={<EditTeam />} />
          <Route
            path="/teams/:teamId/created/*"
            element={<Team createSuccess />}
          />
          <Route
            path="/teams/:teamId/download-team-wheel"
            element={<DownloadTeamWheel type="Team" />}
          />
          <Route path={'/teams/:teamId/*'} element={<Team />} />
          <Route
            path="/account/manage-units"
            element={
              <ProtectedRoute
                requiredPermissions={[
                  { action: 'Unit_Update', scope: 'Global' },
                  { action: 'Unit_ReadAll', scope: 'Global' },
                ]}
              >
                <ManageUnits />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account/transfer-units"
            element={
              <ProtectedRoute
                requiredPermissions={[
                  {
                    action: 'Unit_Transfer',
                    scope: 'Local',
                  },
                ]}
                fallbackComponent={<RestrictedRouteError />}
              >
                <TransferUnits />
              </ProtectedRoute>
            }
          />
          <Route path="/organisations/invite-user" element={<AddUser />} />
          <Route path="/organisations" element={<Organisations />} />
          <Route
            path="/organisations/add"
            element={
              <ProtectedRoute
                requiredPermissions={[
                  { action: 'Organisation_Create', scope: 'Global' },
                ]}
              >
                <AddOrganisation />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organisations/:organisationId"
            element={
              <ProtectedRoute
                requiredPermissions={[
                  { action: 'Organisation_ReadSingle', scope: 'Local' },
                ]}
              >
                <Organisation />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organisations/insights/edit"
            element={
              <ProtectedRoute
                requiredPermissions={[
                  { action: 'Organisation_Insights_Update', scope: 'Global' },
                ]}
              >
                <EditOrganisation />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organisations/:organisationId/edit"
            element={
              <ProtectedRoute
                requiredPermissions={[
                  { action: 'Organisation_Update', scope: 'Local' },
                ]}
              >
                <EditOrganisation />
              </ProtectedRoute>
            }
          />
          <Route
            path="/groups/:groupId/edit"
            element={
              <ProtectedRoute
                requiredPermissions={[
                  { action: 'Organisation_Group_Update', scope: 'Local' },
                ]}
              >
                <EditGroup />
              </ProtectedRoute>
            }
          />
          <Route
            path="/groups/add"
            element={
              <ProtectedRoute
                requiredPermissions={[
                  { action: 'Organisation_Group_Create', scope: 'Global' },
                ]}
              >
                <CreateGroup />
              </ProtectedRoute>
            }
          />
          <Route
            path="/groups/:groupId"
            element={
              <ProtectedRoute
                requiredPermissions={[
                  { action: 'Organisation_Group_ReadSingle', scope: 'Local' },
                ]}
              >
                <Group />
              </ProtectedRoute>
            }
          />
          <Route
            path="/groups/:groupId/create-organisation"
            element={
              <ProtectedRoute
                requiredPermissions={[
                  {
                    action: 'Organisation_Group_Create_Organisation_In_Group',
                    scope: 'Local',
                  },
                ]}
              >
                <CreateGroupOrganisation />
              </ProtectedRoute>
            }
          />
          <Route path="/learners" element={<Learners />} />
          <Route
            path="/learners/:organisationId/:learnerId/edit"
            element={
              <ProtectedRoute
                requiredPermissions={[
                  { action: 'Learner_Update', scope: 'Local' },
                ]}
              >
                <EditLearner />
              </ProtectedRoute>
            }
          />
          <Route
            path="/learners/:organisationId/:learnerId/*"
            element={
              <ProtectedRoute
                requiredPermissions={[
                  { action: 'Learner_ReadSingle', scope: 'Local' },
                ]}
              >
                <Learner />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organisations/:organisationId/practitioners/:practitionerId/edit"
            element={
              <ProtectedRoute
                requiredPermissions={[
                  { action: 'Practitioner_Update', scope: 'Local' },
                ]}
              >
                <EditPractitioner />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organisations/:organisationId/practitioners/:practitionerId/invite"
            element={
              <ProtectedRoute
                requiredPermissions={[
                  {
                    action: 'Add_User_to_Partner_Organisation',
                    scope: 'Global',
                  },
                ]}
              >
                <AddPractitionerToOrgs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organisations/:organisationId/practitioners/:practitionerId"
            element={
              <ProtectedRoute
                requiredPermissions={[
                  { action: 'Practitioner_ReadSingle', scope: 'Local' },
                ]}
              >
                <PractitionerAccount />
              </ProtectedRoute>
            }
          />
          <Route path="/account/edit" element={<EditAccount />} />
          <Route path="/account" element={<Account />} />
          <Route path="/account/change-password" element={<ChangePassword />} />
          <Route path="/evaluator-links" element={<EvaluatorLinks />} />
          <Route
            path="/evaluator-links/:evaluatorLinkId/*"
            element={<CompletedEvaluators />}
          />
          <Route
            path="/evaluator-links/:evaluatorLinkId/:organisationId/:learnerId/*"
            element={<EvaluatorLinkLearner />}
          />

          <Route
            path="/evaluator-links/:evaluatorLinkId/:organisationId/:learnerId/edit"
            element={<EditEvaluatorLinkLearner />}
          />
          <Route path="/signin/password" element={<HandleUnknownRoute />} />
          <Route path="/sso/redirect" element={<HandleUnknownRoute />} />
          <Route path="*" element={<Navigate to={DEFAULT_ROUTE} replace />} />
        </Routes>
      </StyledDiv>
      {!location.pathname.includes('/contact') && (
        <FloatingButtonPosition>
          <FloatingButton
            icon={<QuestionMark />}
            text={t('ui.event-management.contact.floating-button')}
            onClick={() => navigate('/contact')}
          />
        </FloatingButtonPosition>
      )}
      <Footer preferredDialect={preferredDialect} />
    </>
  );
};

const AppRoutes = () => {
  useGATrackPage();
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const location = useLocation();
  const isBetaEnabled = useBetaEnabled('supported-languages');

  useEffect(() => {
    if (
      isPreferredDialect(user?.preferredDialect, isBetaEnabled) &&
      i18n.language !== user?.preferredDialect
    ) {
      i18n.changeLanguage(user?.preferredDialect);
    } else if (
      !isPreferredDialect(user?.preferredDialect, isBetaEnabled) &&
      i18n.language !== DIALECT_CODES.ENGLISH
    ) {
      i18n.changeLanguage(DIALECT_CODES.ENGLISH);
    }
  }, [user?.preferredDialect, i18n, isBetaEnabled]);

  if (location.pathname.length > 1 && location.pathname.endsWith('/')) {
    return <Navigate to={location.pathname.slice(0, -1)} />;
  }

  return user ? (
    <AuthenticatedRoutes preferredDialect={user.preferredDialect || 'en-GB'} />
  ) : (
    <UnauthenticatedRoutes />
  );
};

export default AppRoutes;
